import React, { useState, useEffect, useMemo, useCallback } from 'react';
import projectService, { Project } from '../services/projectService';

interface ProjectSelectorProps {
  onProjectSelect?: (project: Project | null) => void;
  onProjectChange?: (project: Project | null) => void;  // Legacy prop name for backwards compatibility
  selectedProject: Project | null;
  showFilters?: boolean;
  onSubmit?: () => void;  // Optional submit callback for dashboard use case
}

/**
 * ProjectSelector Component
 * Displays a list of projects with search and filter capabilities
 * Similar to opscosttrack project list UI
 * 
 * Supports both:
 * - New project list interface (onProjectSelect)
 * - Legacy dashboard interface (onProjectChange + onSubmit)
 */
const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  onProjectSelect,
  onProjectChange,
  selectedProject,
  showFilters = true,
  onSubmit,
}) => {
  // Use either prop name for backwards compatibility
  const handleProjectChange = onProjectSelect || onProjectChange;
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortBy, setSortBy] = useState<'name' | 'created'>('name');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setProjects(await projectService.getMyPoProjects());
    } catch (err: any) {
      setError(err?.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortBy]);

  // Filtered and sorted projects
  const filteredProjects = useMemo(() => {
    let filtered = [...projects];

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (project) =>
          project.name.toLowerCase().includes(searchLower) ||
          project.description?.toLowerCase().includes(searchLower) ||
          project.identifier?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter((project) => project.status === 1);
    } else if (statusFilter === 'archived') {
      filtered = filtered.filter((project) => project.status === 5);
    }
    // 'all' shows everything

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else if (sortBy === 'created') {
        const dateA = a.created_on ? new Date(a.created_on).getTime() : 0;
        const dateB = b.created_on ? new Date(b.created_on).getTime() : 0;
        return dateB - dateA; // Newest first
      }
      return 0;
    });

    return filtered;
  }, [projects, searchTerm, statusFilter, sortBy]);

  const countLabel = filteredProjects.length === 1 ? 'project' : 'projects';

  // Paginated projects
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredProjects.slice(startIndex, endIndex);
  }, [filteredProjects, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to top of project list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getStatusBadge = (status: number) => {
    const statusLabel = projectService.getStatusLabel(status);
    const baseClass = 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium';

    switch (status) {
      case 1:
        return <span className={`${baseClass} bg-green-100 text-green-800`}>●{statusLabel}</span>;
      case 2:
        return <span className={`${baseClass} bg-yellow-100 text-yellow-800`}>●{statusLabel}</span>;
      case 3:
        return <span className={`${baseClass} bg-gray-100 text-gray-800`}>●{statusLabel}</span>;
      case 5:
        return <span className={`${baseClass} bg-red-100 text-red-800`}>●{statusLabel}</span>;
      default:
        return <span className={`${baseClass} bg-gray-100 text-gray-800`}>●{statusLabel}</span>;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6" role="status" aria-live="polite">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" aria-hidden="true"></div>
          <p className="ml-3 text-gray-600">Loading projects...</p>
          <span className="sr-only">Loading projects, please wait</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert" aria-live="polite">
          <p className="text-red-800 font-medium">Error loading projects</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={loadProjects}
            aria-label="Retry loading projects"
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 text-sm font-medium transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/50">
      {/* Compact Header with Filters */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-900">
            My Projects
          </h2>
          <span className="text-sm text-gray-600">
            {filteredProjects.length} {countLabel}
          </span>
        </div>

        {/* Compact Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {/* Search */}
            <div className="md:col-span-2">
              <label htmlFor="project-search" className="sr-only">
                Search projects
              </label>
              <input
                id="project-search"
                type="text"
                placeholder="Search projects by name, description, or identifier..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search projects"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label htmlFor="status-filter" className="sr-only">
                Filter by status
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter projects by status"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
              >
                <option value="active">Active</option>
                <option value="all">All</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Sort By */}
            <div>
              <label htmlFor="sort-filter" className="sr-only">
                Sort projects
              </label>
              <select
                id="sort-filter"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as 'name' | 'created')}
                aria-label="Sort projects"
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
              >
                <option value="name">Name (A-Z)</option>
                <option value="created">Recent</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Compact Projects List */}
      <div className="divide-y divide-gray-200 max-h-[300px] overflow-y-auto">
        {filteredProjects.length === 0 ? (
          <div className="p-6 text-center" role="status">
            <svg
              className="mx-auto h-10 w-10 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <p className="mt-3 text-sm text-gray-600 font-medium">
              No projects found
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {searchTerm
                ? 'Try adjusting your search or filters'
                : 'You are not assigned to any projects'}
            </p>
          </div>
        ) : (
          paginatedProjects.map((project) => (
            <div
              key={project.id}
              onClick={() => {
                if (handleProjectChange) {
                  handleProjectChange(project);
                }
                // Call onSubmit if provided (for dashboard use case)
                if (onSubmit) {
                  onSubmit();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Select project ${project.name}`}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (handleProjectChange) {
                    handleProjectChange(project);
                  }
                  if (onSubmit) {
                    onSubmit();
                  }
                }
              }}
              className={`p-3 cursor-pointer transition-all hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded ${
                selectedProject?.id === project.id ? 'bg-blue-50 border-l-4 border-blue-600 pl-2.5' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-sm font-medium text-gray-900 truncate">{project.name}</h3>
                    {getStatusBadge(project.status)}
                  </div>
                  {project.description && (
                    <p className="mt-1 text-xs text-gray-600 line-clamp-1">{project.description}</p>
                  )}
                  <div className="mt-1.5 flex items-center space-x-3 text-xs text-gray-500">
                    {project.identifier && (
                      <span className="flex items-center">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                        </svg>
                        {project.identifier}
                      </span>
                    )}
                    {project.role_name && (
                      <span className="flex items-center truncate">
                        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {project.role_name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="ml-3 flex-shrink-0">
                  {selectedProject?.id === project.id && (
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination Footer */}
      {filteredProjects.length > 0 && (
        <div className="p-3 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            {/* Items per page selector */}
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-600">Show:</span>
              <label htmlFor="items-per-page" className="sr-only">
                Items per page
              </label>
              <select
                id="items-per-page"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                aria-label="Number of items per page"
                className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span className="text-xs text-gray-600">
                {Math.min((currentPage - 1) * itemsPerPage + 1, filteredProjects.length)}-
                {Math.min(currentPage * itemsPerPage, filteredProjects.length)} of {filteredProjects.length}
              </span>
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Go to previous page"
                  className="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <span aria-hidden="true">‹</span>
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum = 1;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      aria-label={`Go to page ${pageNum}`}
                      aria-current={currentPage === pageNum ? 'page' : undefined}
                      className={`px-2.5 py-1 text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  aria-label="Go to next page"
                  className="px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <span aria-hidden="true">›</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSelector;
